import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Switch,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
  Chip
} from '@mui/material'
import {
  PictureAsPdf as PdfIcon,
  Sell as SellIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  AutoAwesome as SparklesIcon
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import apiClient from '../../services/api'

export interface CatalogPdfExportDialogProps {
  open: boolean
  onClose: () => void
  targetType: 'category' | 'collection' | 'catalogue'
  targetId: string
  targetName: string
}

export const CatalogPdfExportDialog: React.FC<CatalogPdfExportDialogProps> = ({
  open,
  onClose,
  targetType,
  targetId,
  targetName
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [withPrice, setWithPrice] = useState<boolean>(true)
  const [pricingTier, setPricingTier] = useState<'sale_price' | 'wholesale_price'>('sale_price')
  const [includeImages, setIncludeImages] = useState<boolean>(true)
  const [customTitle, setCustomTitle] = useState<string>(targetName)
  const [loading, setLoading] = useState<boolean>(false)

  const handleDownload = async () => {
    setLoading(true)
    try {
      let endpoint = ''
      let payload: any = {
        with_price: withPrice,
        pricing_tier: pricingTier,
        include_images: includeImages,
        title: customTitle || targetName
      }

      if (targetType === 'category') {
        endpoint = `/admin/categories/${targetId}/export-pdf`
      } else if (targetType === 'collection') {
        endpoint = `/admin/collections/${targetId}/export-pdf`
      } else if (targetType === 'catalogue') {
        endpoint = `/admin/catalogues/${targetId}/export`
        payload.format = 'pdf'
      }

      const response = await apiClient.post(endpoint, payload, {
        responseType: 'blob'
      })

      // Trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const modeTag = withPrice ? 'with-rates' : 'showcase'
      const cleanName = targetName.toLowerCase().replace(/[^a-z0-9]/g, '_')
      a.download = `${targetType}_${cleanName}_${modeTag}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      enqueueSnackbar('PDF Catalog generated & downloaded successfully!', { variant: 'success' })
      onClose()
    } catch (err: any) {
      console.error('PDF export error:', err)
      enqueueSnackbar(
        err?.response?.data?.detail || 'Failed to generate PDF catalog. Please ensure products exist in this selection.',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#dc2626'
          }}
        >
          <PdfIcon />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Generate PDF Catalog
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {targetType.toUpperCase()}: <strong>{targetName}</strong>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2.5 }}>
        <Stack spacing={3}>
          {/* Price Mode Choice */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                Catalog Price Mode
              </FormLabel>
              <RadioGroup
                value={withPrice ? 'with_price' : 'without_price'}
                onChange={(e) => setWithPrice(e.target.value === 'with_price')}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    borderColor: withPrice ? 'primary.main' : 'divider',
                    bgcolor: withPrice ? 'rgba(25, 118, 210, 0.04)' : 'white',
                    cursor: 'pointer'
                  }}
                  onClick={() => setWithPrice(true)}
                >
                  <FormControlLabel
                    value="with_price"
                    control={<Radio />}
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            With Pricing (Wholesale & MRP Rates)
                          </Typography>
                          <Chip label="B2B Standard" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Includes MRP, Wholesale / Sale rates, GST %, MOQ, and master carton rate.
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: !withPrice ? 'success.main' : 'divider',
                    bgcolor: !withPrice ? 'rgba(46, 125, 50, 0.04)' : 'white',
                    cursor: 'pointer'
                  }}
                  onClick={() => setWithPrice(false)}
                >
                  <FormControlLabel
                    value="without_price"
                    control={<Radio />}
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Without Price (Product Showcase / Reseller Mode)
                          </Typography>
                          <Chip label="Showcase" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Completely hides all prices. Displays specs, MOQ, pack sizes, and images. Perfect for resellers to share with end clients.
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </RadioGroup>
            </FormControl>

            {/* Pricing Tier (only shown if withPrice is true) */}
            {withPrice && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                    Select Pricing Tier:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={pricingTier}
                    onChange={(e) => setPricingTier(e.target.value as any)}
                  >
                    <FormControlLabel
                      value="sale_price"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Standard Sale Price</Typography>}
                    />
                    <FormControlLabel
                      value="wholesale_price"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Special Wholesale Price</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </Paper>

          {/* Options */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Catalog Title on PDF"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              fullWidth
              size="small"
              helperText="This title will appear at the top of the generated PDF."
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ImageIcon color="action" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Include Product Thumbnail Images
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Embeds product images directly in the PDF cards
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={includeImages}
                onChange={(e) => setIncludeImages(e.target.checked)}
                color="primary"
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDownload}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          sx={{ fontWeight: 700, px: 3 }}
        >
          {loading ? 'Generating PDF...' : 'Download PDF Catalog'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
